import {
  authHandlers,
  callApi,
  ensureTrailingSlash,
  prefixPath,
  stripTrailingSlash,
  useOnSuccessQuery,
  useQuery,
} from '@wyz/utils';
import { FullPageSpinner } from '@wyz/ui';
import * as React from 'react';
import { BootstrapConfig, I18nConfig, IGlobalConfig } from '~/common/types';
import { loadBootstrapConfig } from '~/common/helpers';
// eslint-disable-next-line @wyz/module/no-relative-import
import { ConfigContext } from './contexts';

export function ConfigProvider(props: React.PropsWithChildren) {
  const call = callApi({ excludeCustomHeaders: true });

  const globalConfig = useQuery<BootstrapConfig>({
    queryKey: ['global-config'],
    queryFn: () => loadBootstrapConfig(call),
    gcTime: Infinity,
    meta: {},
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  useOnSuccessQuery(globalConfig, (config) => {
    // eslint-disable-next-line no-console
    console.log('Config loaded', config);
    window._WYZ_PLUGIN_ = {
      config: config as unknown as IGlobalConfig,
    };
    if (config.tenant) {
      authHandlers().setTenant(config.tenant);
    }
    if (config.tenantName) {
      // No need for third party libs for only the title,
      // if we will need more dynamic stuff in the head tag, we'll start using react helmet
      document.title = config.tenantName;
    }
  });

  const appCdnUrl = stripTrailingSlash(globalConfig.data?.appCdnUrl ?? '');
  const i18nConfigUrl = prefixPath(appCdnUrl, '/i18n_config.json');

  const i18nConfig = useQuery<I18nConfig>({
    queryKey: ['i18n-config', i18nConfigUrl],
    queryFn: () =>
      call<I18nConfig>(i18nConfigUrl, {
        authless: true,
      }),
    gcTime: Infinity,
    meta: {},
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: globalConfig.isSuccess,
  });

  if (!globalConfig.isSettled) {
    return <FullPageSpinner />;
  }

  if (!globalConfig.isSuccess) {
    return <>Failed to fetch config</>;
  }

  if (!i18nConfig.isSettled) {
    return <FullPageSpinner />;
  }

  if (!i18nConfig.isSuccess) {
    return <>Failed to fetch i18n config</>;
  }

  const routingBasename = ensureTrailingSlash(
    globalConfig.data.routingBasename || '',
  );

  return (
    <ConfigContext.Provider
      value={{
        ...globalConfig.data,
        tenantCode: globalConfig.data.tenant,
        i18nConfig: i18nConfig.data,
        routingBasename,
        baseAppUrl: `${location.origin}${routingBasename}`,
        withRoutingBasename: (path: string) =>
          prefixPath(routingBasename, path),
        withCdnUrl: (path: string) => prefixPath(appCdnUrl, path),
      }}
    >
      {props.children}
    </ConfigContext.Provider>
  );
}
export const useConfig = () => {
  const context = React.useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigContext');
  }
  return context;
};
